import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Tab,
  TextArea
} from 'semantic-ui-react';
import CustomDatePicker from '../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../components/MaskedInput';
import { FieldLabel } from '../../../../styles';
import { DATE_INPUT_MASK, maskMoney } from '../../../../services/masks';
import { get } from '../../../../services/storage';
import { UserRolesEnum } from '../../../../enums/user-roles.enum';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../services/notification';
import { updateFireInsurancePolicyAdmin } from '../../../../services/fire-insurance-policy';
import { proposalStatusList, translateProposalStatus } from '../../utils/Pottencial';
import ConfirmPolicyChangesModal from './ConfirmPolicyChangesModal';
import {
  AdminEditPolicyModalData,
  CoverageFormItem,
  PolicyFormState
} from './types';
import {
  CANCELLATION_STATUSES,
  PAYMENT_TYPE_OPTIONS,
  buildUpdatePayload,
  computeFieldDiffs,
  getDirtyTabs,
  isTransitioningToCancellation,
  isWarningVisible,
  mapPolicyToFormState,
  summarizeDiffs,
  validateForm
} from './utils';
import {
  CoverageRow,
  MoneyInputWrapper,
  TabDirtyDot
} from './styles';

type AdminEditPolicyModalProps = {
  open: boolean;
  close: () => void;
  data?: AdminEditPolicyModalData;
  onSuccess: () => void;
};

const AdminEditPolicyModal = ({
  open,
  close,
  data,
  onSuccess
}: AdminEditPolicyModalProps) => {
  const userRole = get('role');
  const isBrokerAdmin = userRole === UserRolesEnum.brokerAdmin;

  const [form, setForm] = useState<PolicyFormState | null>(null);
  const [original, setOriginal] = useState<PolicyFormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    if (!open || !data) {
      return;
    }
    const mapped = mapPolicyToFormState(data);
    setForm(mapped);
    setOriginal(mapped);
    setConfirmOpen(false);
    setActiveTabIndex(0);
  }, [open, data]);

  const diffs = useMemo(() => {
    if (!form || !original) {
      return [];
    }
    return computeFieldDiffs(original, form, translateProposalStatus);
  }, [form, original]);

  const dirtyTabs = useMemo(() => getDirtyTabs(diffs), [diffs]);
  const isCancellation =
    !!form && !!original && isTransitioningToCancellation(original.status, form.status);
  const isAlreadyCanceled =
    !!form && CANCELLATION_STATUSES.includes(form.status) && !isCancellation;
  const hasChanges = diffs.length > 0;

  const updateField = <K extends keyof PolicyFormState>(
    key: K,
    value: PolicyFormState[K]
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateCoverage = (
    index: number,
    patch: Partial<CoverageFormItem>
  ) => {
    setForm((prev) => {
      if (!prev) {
        return prev;
      }
      const coverages = prev.coverages.map((coverage, i) =>
        i === index ? { ...coverage, ...patch } : coverage
      );
      return { ...prev, coverages };
    });
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setConfirmOpen(false);
    close();
  };

  const handleSaveClick = () => {
    if (!form || !original || !isBrokerAdmin) {
      return;
    }

    const validationError = validateForm(form, original.status);
    if (validationError) {
      warningNotification('Opss...', validationError);
      if (validationError.toLowerCase().includes('cancelamento')) {
        setActiveTabIndex(3);
      } else if (validationError.toLowerCase().includes('aviso')) {
        setActiveTabIndex(0);
      }
      return;
    }

    if (!hasChanges) {
      warningNotification('Opss...', 'Nenhuma alteração para salvar.');
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!form || !original) {
      return;
    }

    setIsLoading(true);
    try {
      await updateFireInsurancePolicyAdmin(
        form.policyId,
        buildUpdatePayload(form, original.status)
      );
      successNotification(
        'Sucesso',
        `Apólice atualizada: ${summarizeDiffs(diffs)}.`
      );
      setConfirmOpen(false);
      onSuccess();
      close();
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as any)?.message || 'Não foi possível atualizar a apólice.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderMoneyInput = (
    label: string,
    value: string,
    onChange: (value: string) => void
  ) => (
    <Grid.Column width={8}>
      <FieldLabel>{label}</FieldLabel>
      <MoneyInputWrapper>
        <Input
          fluid
          disabled={!isBrokerAdmin || isLoading}
          value={value}
          onChange={(_, { value: next }) => onChange(maskMoney(String(next)))}
        />
      </MoneyInputWrapper>
    </Grid.Column>
  );

  const identificationPane = (
    <Tab.Pane attached={false}>
      {!isBrokerAdmin && (
        <Message negative>
          Apenas administradores do corretor podem editar apólices por esta
          modal.
        </Message>
      )}

      {isCancellation && (
        <Message warning>
          Status de cancelamento selecionado. Preencha a aba{' '}
          <strong>Cancelamento</strong> com o motivo obrigatório.
        </Message>
      )}

      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <Message warning={isWarningVisible(form?.validationStatus)}>
              <Message.Header>
                <Icon name="warning sign" /> Aviso na listagem
              </Message.Header>
              <div style={{ marginTop: 12 }}>
                <Checkbox
                  label="Exibir aviso na listagem"
                  checked={isWarningVisible(form?.validationStatus)}
                  disabled={!isBrokerAdmin || isLoading}
                  onChange={(_, { checked }) => {
                    setForm((prev) => {
                      if (!prev) {
                        return prev;
                      }
                      if (checked) {
                        return {
                          ...prev,
                          validationStatus:
                            original?.validationStatus === 'error'
                              ? 'error'
                              : 'warning'
                        };
                      }
                      return {
                        ...prev,
                        validationStatus: '',
                        validationMessage: ''
                      };
                    });
                  }}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <FieldLabel required={isWarningVisible(form?.validationStatus)}>
                  Mensagem do aviso
                </FieldLabel>
                <Form>
                  <TextArea
                    rows={3}
                    disabled={
                      !isBrokerAdmin ||
                      isLoading ||
                      !isWarningVisible(form?.validationStatus)
                    }
                    placeholder="Texto exibido no popup do ícone amarelo da listagem"
                    value={form?.validationMessage || ''}
                    onChange={(_, { value }) =>
                      updateField('validationMessage', String(value || ''))
                    }
                  />
                </Form>
              </div>
            </Message>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel required>Status</FieldLabel>
            <Dropdown
              fluid
              selection
              search
              disabled={!isBrokerAdmin || isLoading}
              options={proposalStatusList}
              value={form?.status || ''}
              onChange={(_, { value }) => {
                updateField('status', String(value || ''));
                if (CANCELLATION_STATUSES.includes(String(value || ''))) {
                  setActiveTabIndex(3);
                }
              }}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>Número da apólice</FieldLabel>
            <Input
              fluid
              disabled={!isBrokerAdmin || isLoading}
              value={form?.policyNumber || ''}
              onChange={(_, { value }) => updateField('policyNumber', value)}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Código externo da proposta</FieldLabel>
            <Input
              fluid
              disabled={!isBrokerAdmin || isLoading}
              value={form?.externalProposalCode || ''}
              onChange={(_, { value }) =>
                updateField('externalProposalCode', value)
              }
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>Código externo da apólice</FieldLabel>
            <Input
              fluid
              disabled={!isBrokerAdmin || isLoading}
              value={form?.externalPolicyCode || ''}
              onChange={(_, { value }) =>
                updateField('externalPolicyCode', value)
              }
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Código de referência</FieldLabel>
            <Input
              fluid
              disabled={!isBrokerAdmin || isLoading}
              value={form?.referenceCode || ''}
              onChange={(_, { value }) => updateField('referenceCode', value)}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>Tipo de pagamento</FieldLabel>
            <Dropdown
              fluid
              selection
              clearable
              disabled={!isBrokerAdmin || isLoading}
              options={PAYMENT_TYPE_OPTIONS}
              value={form?.paymentType || ''}
              onChange={(_, { value }) =>
                updateField('paymentType', String(value || ''))
              }
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Parcelas (apólice)</FieldLabel>
            <Input
              fluid
              type="number"
              min={1}
              disabled={!isBrokerAdmin || isLoading}
              value={form?.installments || ''}
              onChange={(_, { value }) => updateField('installments', value)}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <FieldLabel>Início da vigência</FieldLabel>
            <CustomDatePicker
              selected={form?.policyPeriodStart}
              disabled={!isBrokerAdmin || isLoading}
              onChange={(date: Date | null) =>
                updateField('policyPeriodStart', date)
              }
              dateFormat="dd/MM/yyyy"
              customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldLabel>Fim da vigência</FieldLabel>
            <CustomDatePicker
              selected={form?.policyPeriodEnd}
              disabled={!isBrokerAdmin || isLoading}
              onChange={(date: Date | null) =>
                updateField('policyPeriodEnd', date)
              }
              dateFormat="dd/MM/yyyy"
              customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <FieldLabel>Observações</FieldLabel>
            <Form>
              <TextArea
                rows={3}
                disabled={!isBrokerAdmin || isLoading}
                value={form?.observations || ''}
                onChange={(_, { value }) =>
                  updateField('observations', String(value || ''))
                }
              />
            </Form>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Tab.Pane>
  );

  const valuesPane = (
    <Tab.Pane attached={false}>
      <Grid>
        <Grid.Row>
          {renderMoneyInput('Prêmio comercial', form?.commercialPremium || '', (v) =>
            updateField('commercialPremium', v)
          )}
          {renderMoneyInput('Prêmio bruto', form?.grossPremium || '', (v) =>
            updateField('grossPremium', v)
          )}
        </Grid.Row>
        <Grid.Row>
          {renderMoneyInput('Prêmio mínimo', form?.minPremium || '', (v) =>
            updateField('minPremium', v)
          )}
          {renderMoneyInput('Prêmio máximo', form?.maxPremium || '', (v) =>
            updateField('maxPremium', v)
          )}
        </Grid.Row>
        <Grid.Row>
          {renderMoneyInput('IOF', form?.iof || '', (v) => updateField('iof', v))}
          {renderMoneyInput('Assistência', form?.assistanceValue || '', (v) =>
            updateField('assistanceValue', v)
          )}
        </Grid.Row>
        <Grid.Row>
          {renderMoneyInput('Valor da parcela', form?.value || '', (v) =>
            updateField('value', v)
          )}
          <Grid.Column width={8}>
            <FieldLabel>Parcelas (análise)</FieldLabel>
            <Input
              fluid
              type="number"
              min={1}
              disabled={!isBrokerAdmin || isLoading}
              value={form?.analysisInstallments || ''}
              onChange={(_, { value }) =>
                updateField('analysisInstallments', value)
              }
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Tab.Pane>
  );

  const coveragesPane = (
    <Tab.Pane attached={false}>
      {(form?.coverages || []).map((coverage, index) => (
        <CoverageRow key={coverage.id || `coverage-${index}`} $disabled={!coverage.enabled}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={10} verticalAlign="middle">
                <FieldLabel>{coverage.coverageName || 'Cobertura'}</FieldLabel>
              </Grid.Column>
              <Grid.Column width={6} verticalAlign="middle">
                <Checkbox
                  label="Ativa"
                  checked={coverage.enabled}
                  disabled={!isBrokerAdmin || isLoading}
                  onChange={(_, { checked }) =>
                    updateCoverage(index, { enabled: !!checked })
                  }
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <FieldLabel required>Valor</FieldLabel>
                <MoneyInputWrapper>
                  <Input
                    fluid
                    disabled={!isBrokerAdmin || isLoading || !coverage.enabled}
                    value={coverage.value}
                    onChange={(_, { value }) =>
                      updateCoverage(index, { value: maskMoney(String(value)) })
                    }
                  />
                </MoneyInputWrapper>
              </Grid.Column>
              <Grid.Column width={8}>
                <FieldLabel>Prêmio</FieldLabel>
                <MoneyInputWrapper>
                  <Input
                    fluid
                    disabled={!isBrokerAdmin || isLoading || !coverage.enabled}
                    value={coverage.totalPremium}
                    onChange={(_, { value }) =>
                      updateCoverage(index, {
                        totalPremium: maskMoney(String(value))
                      })
                    }
                  />
                </MoneyInputWrapper>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </CoverageRow>
      ))}

      {(form?.coverages || []).length === 0 && (
        <Message info>Nenhuma cobertura cadastrada nesta apólice.</Message>
      )}
    </Tab.Pane>
  );

  const cancellationPane = (
    <Tab.Pane attached={false}>
      {isAlreadyCanceled ? (
        <Message info>
          Esta apólice já está cancelada. Estes campos ficam disponíveis apenas
          ao alterar o status para um status de cancelamento nesta edição.
        </Message>
      ) : !isCancellation ? (
        <Message info>
          Selecione um status de cancelamento na aba Identificação para habilitar
          estes campos.
        </Message>
      ) : (
        <>
          <Message negative>
            <Icon name="warning sign" />
            Cancelamento exige motivo. Esta ação registra o cancelamento
            administrativamente.
          </Message>
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <FieldLabel required>Motivo do cancelamento</FieldLabel>
                <Form>
                  <TextArea
                    rows={4}
                    disabled={!isBrokerAdmin || isLoading}
                    value={form?.cancellationReason || ''}
                    onChange={(_, { value }) =>
                      updateField('cancellationReason', String(value || ''))
                    }
                  />
                </Form>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <FieldLabel>Data do cancelamento</FieldLabel>
                <CustomDatePicker
                  selected={form?.canceledAt}
                  disabled={!isBrokerAdmin || isLoading}
                  onChange={(date: Date | null) => updateField('canceledAt', date)}
                  dateFormat="dd/MM/yyyy"
                  customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <FieldLabel>Valor de reembolso</FieldLabel>
                <MoneyInputWrapper>
                  <Input
                    fluid
                    disabled={!isBrokerAdmin || isLoading}
                    value={form?.refundValue || ''}
                    onChange={(_, { value }) =>
                      updateField('refundValue', maskMoney(String(value)))
                    }
                  />
                </MoneyInputWrapper>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <FieldLabel>Observações do cancelamento</FieldLabel>
                <Form>
                  <TextArea
                    rows={3}
                    disabled={!isBrokerAdmin || isLoading}
                    value={form?.cancellationObservations || ''}
                    onChange={(_, { value }) =>
                      updateField(
                        'cancellationObservations',
                        String(value || '')
                      )
                    }
                  />
                </Form>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}
    </Tab.Pane>
  );

  const panes = [
    {
      menuItem: {
        key: 'identification',
        content: (
          <span>
            Identificação
            {dirtyTabs.identification ? <TabDirtyDot /> : null}
          </span>
        )
      },
      render: () => identificationPane
    },
    {
      menuItem: {
        key: 'values',
        content: (
          <span>
            Valores
            {dirtyTabs.values ? <TabDirtyDot /> : null}
          </span>
        )
      },
      render: () => valuesPane
    },
    {
      menuItem: {
        key: 'coverages',
        content: (
          <span>
            Coberturas
            {dirtyTabs.coverages ? <TabDirtyDot /> : null}
          </span>
        )
      },
      render: () => coveragesPane
    },
    {
      menuItem: {
        key: 'cancellation',
        content: (
          <span>
            Cancelamento
            {dirtyTabs.cancellation ? <TabDirtyDot /> : null}
          </span>
        )
      },
      render: () => cancellationPane
    }
  ];

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        size="large"
        closeOnEscape={!isLoading}
        closeOnDimmerClick={false}
      >
        <Header icon="edit" content="Editar Apólice (Admin)" />
        <Modal.Content scrolling>
          <Dimmer active={isLoading} inverted>
            <Loader />
          </Dimmer>

          {form ? (
            <Tab
              menu={{ secondary: true, pointing: true }}
              panes={panes}
              activeIndex={activeTabIndex}
              onTabChange={(_, dataTab) =>
                setActiveTabIndex(Number(dataTab.activeIndex || 0))
              }
            />
          ) : (
            <Loader active inline="centered" />
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="red" disabled={isLoading} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            color="green"
            disabled={isLoading || !isBrokerAdmin || !hasChanges}
            onClick={handleSaveClick}
          >
            Salvar
          </Button>
        </Modal.Actions>
      </Modal>

      <ConfirmPolicyChangesModal
        open={confirmOpen}
        diffs={diffs}
        isLoading={isLoading}
        isCancellation={isCancellation}
        cancellationReason={form?.cancellationReason}
        refundValue={form?.refundValue}
        onBack={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  );
};

export default AdminEditPolicyModal;
export type { AdminEditPolicyModalData };
